'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable tailwindcss/classnames-order */
import classNames from 'classnames';
import { useCallback, useMemo, useRef } from 'react';
import Select, {
  ClassNamesConfig,
  components as baseSelectComponents,
  ControlProps,
  InputProps,
  MultiValueProps,
  MultiValueRemoveProps,
  OptionProps,
  StylesConfig,
} from 'react-select';
import type { CountryChipPickerProps } from './types';
import { FlagImage } from '$/components/CountryChipPicker/FlagImage';
import Close from '$/components/icons/Close';
import ExpandMore from '$/components/icons/ExpandMore';
import Search from '$/components/icons/Search';

const MAX_VISIBLE_VALUES = 2;

const CountryOption = ({ children, ...props }: OptionProps) => {
  const data = props.data as { label: string; value: string };

  return (
    <baseSelectComponents.Option {...props}>
      <FlagImage countryCode={data.value} countryName={data.label} />
      {children}
    </baseSelectComponents.Option>
  );
};

const MultiValueLabel = ({ children, ...props }: MultiValueRemoveProps) => {
  const data = props.data as { label: string; value: string };
  return (
    <baseSelectComponents.MultiValueLabel {...props}>
      <FlagImage countryCode={data.value} countryName={data.label} />
      <span className="overflow-hidden text-ellipsis max-w-full">
        {children}
      </span>
    </baseSelectComponents.MultiValueLabel>
  );
};

const Control = ({ children, ...props }: ControlProps) => {
  return (
    <baseSelectComponents.Control {...props}>
      {props.isMulti && (
        <Search className="size-shapes/icon-size-lg text-buttons/neutral" />
      )}
      {children}
      {!props.isMulti && (
        <ExpandMore
          className={classNames(
            'size-shapes/icon-size-lg text-buttons/neutral-dark',
          )}
        />
      )}
    </baseSelectComponents.Control>
  );
};

const CustomInputForHydrationErrorFix = (props: InputProps) => {
  const newProps = {
    ...props,
    ['aria-activedescendant']: undefined,
  };
  return <baseSelectComponents.Input {...newProps} />;
};

const MultiValueRemove = (props: MultiValueRemoveProps) => (
  <div className="flex">
    <baseSelectComponents.MultiValueRemove {...props}>
      <Close className="size-shapes/icon-size-md hover:text-red-700" />
    </baseSelectComponents.MultiValueRemove>
  </div>
);

const None = () => {
  return null;
};

export function CountryChipPicker({
  mode,
  isMulti,
  options,
  components,
  placeholder,
  maxVisibleValues = MAX_VISIBLE_VALUES,
  instanceId: providedInstanceId,
  inputId: providedInputId,
  ...props
}: CountryChipPickerProps): JSX.Element {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectRef = useRef<any>(null);

  // react-select derives element ids (e.g. the `-live-region` id) from an
  // internal `useId`. `useId` drifts between server and client whenever the
  // surrounding tree diverges, and that drift causes a hydration mismatch on
  // those ids. Feed react-select a stable, caller-supplied id instead, falling
  // back to the `id` / `name` props callers already pass, so server and client
  // render the same ids. Callers should provide a unique `instanceId` / `id` /
  // `name`; without one react-select falls back to its own `useId` (drift risk).
  const instanceId =
    providedInstanceId ??
    (typeof props.id === 'string' ? props.id : undefined) ??
    (typeof props.name === 'string' ? props.name : undefined);
  const inputId =
    providedInputId ?? (instanceId ? `${instanceId}-input` : undefined);

  // Stable component identities — a fresh `components` object (or inline
  // MultiValue) on every render makes react-select remount the chips, which
  // reloads the flag <img>s and causes a visible flicker.
  const renderMultiValue = useCallback(
    (mvProps: MultiValueProps) => {
      const {
        index,
        getValue,
        selectProps: { menuIsOpen },
      } = mvProps;
      const selectedValuesLength = getValue().length;
      if (!menuIsOpen && index > maxVisibleValues) {
        return null;
      }
      if (!menuIsOpen && index === maxVisibleValues) {
        return `+${selectedValuesLength - maxVisibleValues}`;
      }
      return <baseSelectComponents.MultiValue {...mvProps} />;
    },
    [maxVisibleValues],
  );

  const selectComponents = useMemo(
    () => ({
      ClearIndicator: None,
      Control,
      DropdownIndicator: None,
      IndicatorSeparator: None,
      MultiValueRemove,
      MultiValueLabel,
      Input: CustomInputForHydrationErrorFix,
      Option:
        mode === 'countries' ? CountryOption : baseSelectComponents.Option,
      MultiValue: renderMultiValue,
      ...components,
    }),
    [mode, renderMultiValue, components],
  );

  const selectClassNames = useMemo<ClassNamesConfig>(
    () => ({
      container: () => 'z-20',
      control: ({ selectProps: { menuIsOpen } }) =>
        classNames(
          '!mhg-alpha-form-input !min-h-button/size gap-spacing/gap/x-xs !rounded-radius/button !border-borders/form-default !px-form/inner-element-padding !py-1 hover:!border-borders/form-hover hover:shadow-md focus:!border-borders/form-hover',
          {
            '!shadow-md': menuIsOpen,
            '!border-borders/form-hover': menuIsOpen,
          },
        ),
      dropdownIndicator: () => classNames(''),
      input: () => classNames('!rounded-radius/button'),
      menu: () =>
        classNames(
          '!shadow-shadow/dropdown !mt-1 !flex !flex-col !items-end !overflow-hidden !rounded-radius/button !border-none !p-0 !bg-white',
        ),
      menuList: () =>
        classNames(
          '!flex !w-full !flex-col !items-stretch !gap-0 !rounded-radius-[inherit] !p-0',
        ),
      multiValue: () =>
        classNames(
          '!m-0 flex !min-h-form/chip-min-height items-center gap-[--button-gap] !rounded-radius/button !px-spacing/padding/x-xs !py-0 !bg-fill/alternative-2 hover:!bg-fill/alternative-3',
        ),
      multiValueLabel: () =>
        classNames(
          'mhg-alpha-button-2 flex items-center gap-[--button-gap] !p-0',
        ),
      multiValueRemove: () =>
        classNames('self-center !p-0 hover:!bg-transparent'),
      option: ({ isFocused, isSelected }) =>
        classNames(
          '!flex !w-full !cursor-pointer !items-center gap-spacing/gap/soft-12px !py-0 px-spacing/gap/soft-12px !font-semibold !transition-colors !duration-150 text-text/primary !leading-line-height/sm first:!rounded-t-radius-[inherit] last:!rounded-b-radius-[inherit] hover:!bg-fill/alternative-1 hover:!text-[--fill-primary-dark] focus:!outline-none',
          {
            '!min-h-[--form-size]': isMulti,
            '!min-h-[--button-size-small]': !isMulti,
            '!mhg-alpha-typography-input-text': isMulti,
            '!mhg-alpha-button-2': !isMulti,
            '!bg-primary-50 !text-[--fill-primary-dark]': isSelected,
            '!bg-fill/alternative-1 !text-[--fill-primary-dark]': isFocused,
          },
        ),
      placeholder: () => classNames(''),
      valueContainer: ({ selectProps: { menuIsOpen } }) =>
        classNames('gap-[--button-gap] !p-0', {
          '!flex-nowrap': !menuIsOpen,
        }),
    }),
    [isMulti],
  );

  const selectStyles = useMemo<StylesConfig>(
    () => ({
      control: (base) => ({
        ...base,
        boxShadow: 'none',
      }),
    }),
    [],
  );

  return (
    <Select
      {...props}
      ref={selectRef}
      instanceId={instanceId}
      inputId={inputId}
      classNames={selectClassNames}
      components={selectComponents}
      isMulti={isMulti}
      options={options}
      isClearable={!isMulti}
      placeholder={placeholder}
      closeMenuOnSelect={!isMulti}
      blurInputOnSelect={false}
      openMenuOnFocus={false}
      openMenuOnClick={true}
      styles={selectStyles}
    />
  );
}
